import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  productServiceHistoryId,
  type ProductServiceHistoryId,
} from './ProductServiceHistory.js'
import { serviceContractId, type ServiceContractId } from './ServiceContract.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.service_contract_item_ */
export type ServiceContractItemId = string & {
  __brand: 'ServiceContractItemId'
}

/** Represents the table public.service_contract_item_ */
export default interface ServiceContractItemTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ServiceContractItemId,
    ServiceContractItemId | undefined,
    ServiceContractItemId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  serviceContractId: ColumnType<
    ServiceContractId,
    ServiceContractId,
    ServiceContractId
  >

  /** Database type: pg_catalog.timestamptz */
  trialEndedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.timestamptz */
  trialStartedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.timestamptz */
  serviceStartDate: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  serviceEndDate: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.int4 */
  totalPriceInCents: ColumnType<number, number, number>

  /** Database type: pg_catalog.uuid */
  productServiceHistoryId: ColumnType<
    ProductServiceHistoryId,
    ProductServiceHistoryId,
    ProductServiceHistoryId
  >

  /** Database type: pg_catalog.text */
  serviceName: ColumnType<string, string, string>

  /** Database type: pg_catalog.int4 */
  servicePriceInCents: ColumnType<number, number, number>

  /** Database type: pg_catalog.int4 */
  serviceQuantity: ColumnType<number, number, number>

  /** Database type: pg_catalog.int4 */
  serviceDuration: ColumnType<number, number, number>

  /** Database type: pg_catalog.int4 */
  serviceTotalDuration: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  serviceDurationType: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  serviceUnitType: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  serviceExpiredAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.bool */
  rental: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.bool */
  sponsored: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.bool */
  internal: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.timestamptz */
  serviceDateRevenueStartedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  serviceDateRevenueEndedAt: ColumnType<Date, Date | string, Date | string>
}

export type ServiceContractItem = Selectable<ServiceContractItemTable>

export type ServiceContractItemInitializer =
  Insertable<ServiceContractItemTable>

export type ServiceContractItemMutator = Updateable<ServiceContractItemTable>

export const serviceContractItemId =
  z.string() as unknown as z.Schema<ServiceContractItemId>

export const serviceContractItem = z.object({
  id: serviceContractItemId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  serviceContractId: serviceContractId,
  trialEndedAt: z.coerce.date().nullable().optional(),
  trialStartedAt: z.coerce.date().nullable().optional(),
  serviceStartDate: z.coerce.date(),
  serviceEndDate: z.coerce.date(),
  totalPriceInCents: z.number(),
  productServiceHistoryId: productServiceHistoryId,
  serviceName: z.string(),
  servicePriceInCents: z.number(),
  serviceQuantity: z.number(),
  serviceDuration: z.number(),
  serviceTotalDuration: z.number(),
  serviceDurationType: z.string(),
  serviceUnitType: z.string(),
  notes: z.string().nullable().optional(),
  serviceExpiredAt: z.coerce.date(),
  rental: z.boolean(),
  sponsored: z.boolean(),
  internal: z.boolean(),
  serviceDateRevenueStartedAt: z.coerce.date(),
  serviceDateRevenueEndedAt: z.coerce.date(),
}) as unknown as z.Schema<ServiceContractItem>

export const serviceContractItemInitializer = z.object({
  id: serviceContractItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceContractId: serviceContractId,
  trialEndedAt: z.coerce.date().optional().nullable(),
  trialStartedAt: z.coerce.date().optional().nullable(),
  serviceStartDate: z.coerce.date(),
  serviceEndDate: z.coerce.date(),
  totalPriceInCents: z.number(),
  productServiceHistoryId: productServiceHistoryId,
  serviceName: z.string(),
  servicePriceInCents: z.number(),
  serviceQuantity: z.number(),
  serviceDuration: z.number(),
  serviceTotalDuration: z.number(),
  serviceDurationType: z.string(),
  serviceUnitType: z.string(),
  notes: z.string().optional().nullable(),
  serviceExpiredAt: z.coerce.date(),
  rental: z.boolean().optional(),
  sponsored: z.boolean().optional(),
  internal: z.boolean().optional(),
  serviceDateRevenueStartedAt: z.coerce.date(),
  serviceDateRevenueEndedAt: z.coerce.date(),
}) as unknown as z.Schema<ServiceContractItemInitializer>

export const serviceContractItemMutator = z.object({
  id: serviceContractItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceContractId: serviceContractId.optional(),
  trialEndedAt: z.coerce.date().optional().nullable(),
  trialStartedAt: z.coerce.date().optional().nullable(),
  serviceStartDate: z.coerce.date().optional(),
  serviceEndDate: z.coerce.date().optional(),
  totalPriceInCents: z.number().optional(),
  productServiceHistoryId: productServiceHistoryId.optional(),
  serviceName: z.string().optional(),
  servicePriceInCents: z.number().optional(),
  serviceQuantity: z.number().optional(),
  serviceDuration: z.number().optional(),
  serviceTotalDuration: z.number().optional(),
  serviceDurationType: z.string().optional(),
  serviceUnitType: z.string().optional(),
  notes: z.string().optional().nullable(),
  serviceExpiredAt: z.coerce.date().optional(),
  rental: z.boolean().optional(),
  sponsored: z.boolean().optional(),
  internal: z.boolean().optional(),
  serviceDateRevenueStartedAt: z.coerce.date().optional(),
  serviceDateRevenueEndedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<ServiceContractItemMutator>
